/*
 *  (c) 2015 - 2025 ENISCO by FORCAM GmbH
 */

import {TextField} from '@mui/material'
import React, {SyntheticEvent} from 'react'
import {ThemeName, useThemeName, useUserName} from '@enisco/mcc-react-lib'
import {defineMessage, FormattedMessage, MessageDescriptor, useIntl} from 'react-intl'
import useProfile from '../../../../hooks/useProfile'
import Autocomplete from '@mui/material/Autocomplete'


interface ThemeOption {
  name: ThemeName
  description: MessageDescriptor
}

const options : ThemeOption[] = [
  {name: 'system', description: defineMessage({id:'MyProfileDialog.SystemTheme', defaultMessage: 'System'})},
  {name: 'dark', description: defineMessage({id:'MyProfileDialog.DarkTheme', defaultMessage: 'Dark'})},
  {name: 'light', description: defineMessage({id:'MyProfileDialog.LightTheme', defaultMessage: 'Light'})},
]

const ChangeTheme: React.FC = () => {
  const [themeName, setThemeName] = useThemeName()
  const currentOption = options.find((o) => o.name === themeName) ?? 'system'
  const [, setProfile] = useProfile()
  const userName = useUserName()
  const intl = useIntl()

  const onChange = (e: SyntheticEvent, value: ThemeOption | null) => {
    const newTheme = value?.name ?? 'system'
    setThemeName(newTheme)
    if (userName) {
      setProfile({theme: newTheme})
    }
  }

  return (
    <Autocomplete
      disabled={userName === undefined}
      sx={{width: '284px'}}
      value={currentOption as ThemeOption}
      onChange={onChange}
      id='change-theme'
      getOptionLabel={(option) =>
        intl.formatMessage(option.description)
      }
      disableClearable={true}
      options={options}
      isOptionEqualToValue={(option, value) =>
        option.description === value.description
      }
      data-testid={'Apps:autocomplete'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            <FormattedMessage
              id='MyProfileDialog.Theme'
              defaultMessage='Theme'
            />
          }
          InputProps={{...params.InputProps}}
        />
      )}
    />
  )
}

export default ChangeTheme
